'use client';

/**
 * ResizeHandle Component
 *
 * Draggable handle for resizing DetailPanel width.
 */

import { alpha, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface ResizeHandleProps {
  /** Which side of the panel the handle is on */
  anchor: 'left' | 'right';
  /** Callback when resize occurs */
  onResize: (newWidth: number) => void;
  /** Current panel width */
  currentWidth: number;
  /** Minimum width constraint */
  minWidth?: number;
  /** Maximum width constraint */
  maxWidth?: number;
}

// ============================================================================
// Styled Components
// ============================================================================

interface HandleContainerProps {
  $anchor: 'left' | 'right';
  $isDragging: boolean;
  $isHovering: boolean;
}

const HandleContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<HandleContainerProps>(({ theme, $anchor, $isDragging, $isHovering }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  [$anchor]: 0,
  width: 10,
  cursor: 'col-resize',
  userSelect: 'none',
  touchAction: 'none',
  zIndex: 10,

  // Full-height divider line — always visible to indicate the split boundary
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: $isDragging ? 3 : $isHovering ? 2 : 1,
    backgroundColor: $isDragging
      ? theme.palette.primary.main
      : $isHovering
        ? theme.palette.primary.main
        : theme.palette.divider,
    transition: $isDragging ? 'none' : 'background-color 150ms ease, width 150ms ease',
  },

  // Drag grip indicator — appears on hover/drag to indicate draggability
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 6,
    height: $isDragging ? 48 : $isHovering ? 36 : 0,
    borderRadius: 3,
    backgroundColor: $isDragging ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.7),
    opacity: $isDragging || $isHovering ? 1 : 0,
    transition: $isDragging ? 'none' : 'all 150ms ease',
    boxShadow: $isDragging ? `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}` : 'none',
  },
}));

// ============================================================================
// Component
// ============================================================================

export const ResizeHandle: React.FC<ResizeHandleProps> = ({
  anchor,
  onResize,
  currentWidth,
  minWidth = 320,
  maxWidth = 600,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      startXRef.current = e.clientX;
      startWidthRef.current = currentWidth;

      // Add body class to prevent text selection during drag
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [currentWidth],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = e.clientX - startXRef.current;
      // For left anchor (handle on left of panel), dragging left (negative deltaX) increases width
      // For right anchor (handle on right of panel), dragging right (positive deltaX) increases width
      const newWidth = anchor === 'left' ? startWidthRef.current - deltaX : startWidthRef.current + deltaX;

      // Clamp to min/max
      const clampedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
      onResize(clampedWidth);
    },
    [isDragging, anchor, minWidth, maxWidth, onResize],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Touch handlers for mobile
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return;

      setIsDragging(true);
      startXRef.current = e.touches[0].clientX;
      startWidthRef.current = currentWidth;
    },
    [currentWidth],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging || e.touches.length !== 1) return;

      const deltaX = e.touches[0].clientX - startXRef.current;
      // Same logic as mouse: left anchor drag left = increase, right anchor drag right = increase
      const newWidth = anchor === 'left' ? startWidthRef.current - deltaX : startWidthRef.current + deltaX;

      const clampedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
      onResize(clampedWidth);
    },
    [isDragging, anchor, minWidth, maxWidth, onResize],
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Global event listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  return (
    <HandleContainer
      $anchor={anchor}
      $isDragging={isDragging}
      $isHovering={isHovering}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={currentWidth}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      aria-label="Resize panel"
      tabIndex={0}
      onKeyDown={(e) => {
        // Allow keyboard resizing
        // For left anchor: ArrowLeft = increase width, ArrowRight = decrease width
        // For right anchor: ArrowRight = increase width, ArrowLeft = decrease width
        const step = e.shiftKey ? 50 : 10;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const newWidth = anchor === 'left' ? currentWidth + step : currentWidth - step;
          onResize(Math.min(Math.max(newWidth, minWidth), maxWidth));
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          const newWidth = anchor === 'left' ? currentWidth - step : currentWidth + step;
          onResize(Math.min(Math.max(newWidth, minWidth), maxWidth));
        }
      }}
    />
  );
};

export default ResizeHandle;
